import { isApolloError, ApolloError } from '@apollo/client/core'
import { ErrorHandler, Injectable } from '@angular/core'
import { Router } from '@angular/router'

import { TerminologyService } from '../../terminology.service'
import { CommService } from '../comm.service'
import { getAnalysisFailureMessage, isAnalysisFailure } from './analysisFailure'
import {
  isNetworkError,
  isMissingFieldError,
  isQueryError,
  isNavError,
  isAuthError,
} from './helpers'
import { MessageError, NotFoundError } from './errors'
import { issueTracker } from './issueTracker'
import {
  getUserFacingMessage,
  isUserFacingError,
  wasUserFacingErrorShown,
} from './userFacing'

/**
 * How to add a new type of recognized error:
 * 1. Add to enum below
 * 2. Add a handler to GlobalErrorHandler.handlers
 * 3. Add logic in GlobalErrorHandler.findErrorType to classify your error
 */
enum ErrorType {
  'AuthError',
  'UnknownError',
  'NetworkError',
  'TypeError',
  'MissingFieldError',
  'ApolloError',
  'QueryError',
  'NavError',
  'MessageError',
  'NotFoundError',
  'ServerApolloError',
  'UserFacingError',
  'AnalysisFailure',
}

interface ServerApolloError {
  extensions: any
  locations: any[]
  message: string
  path: string[]
}
function isServerApolloError(e: any): e is ServerApolloError {
  return (
    typeof e === 'object' &&
    e !== null &&
    !!e.extensions &&
    Array.isArray(e.locations) &&
    typeof e.message === 'string' &&
    Array.isArray(e.path)
  )
}

/**
 * What the user sees when something fails: a toast, a redirect, or nothing
 * when the failing flow has already shown its own message. Every error is
 * also reported to the issue tracker (issueTracker.ts), where classify.ts
 * decides its class and group independently of the toast chosen here.
 */
@Injectable()
export class GlobalErrorHandler extends ErrorHandler {
  /**
   * Add a handler here for your error
   */
  private handlers: { [key in ErrorType]: (e: unknown) => void } = {
    [ErrorType.AuthError]: () =>
      this.comm.alert('warning', 'Session expired, please log in again'),
    [ErrorType.QueryError]: () =>
      this.comm.alert('danger', "Query Error: couldn't fetch data"),
    [ErrorType.UnknownError]: () =>
      this.comm.alert(
        'danger',
        'Something unexpected happened. Please try again later',
      ),
    [ErrorType.NetworkError]: () =>
      this.comm.alert(
        'danger',
        'Network Error: Are you connected to the internet?',
      ),
    [ErrorType.TypeError]: () =>
      this.comm.alert(
        'danger',
        'Something went wrong, try refreshing the page',
      ),
    [ErrorType.MissingFieldError]: () =>
      this.comm.alert('danger', 'Please fill out all fields'),
    [ErrorType.ApolloError]: (e: ApolloError) => {
      e.graphQLErrors.forEach((gqle) => {
        const code = gqle.extensions?.code
        if (code === 'INTERNAL_SERVER_ERROR') {
          this.comm.alert(
            'danger',
            'Something went wrong on our end. Please try again later',
          )
        } else {
          // it was a known error
          this.comm.alert('danger', gqle.message) // this message must have been written for the user
        }
      })
    },
    [ErrorType.ServerApolloError]: (e: ServerApolloError) => {
      const code = e.extensions?.code
      if (code === 'INTERNAL_SERVER_ERROR') {
        this.comm.alert(
          'danger',
          'Something went wrong on our end. Please try again later',
        )
      } else {
        // it was a known error
        this.comm.alert('danger', e.message) // this message must have been written for the user
      }
    },
    [ErrorType.NavError]: () =>
      // there's probably a better way to do this
      this.router.navigateByUrl(''),
    [ErrorType.MessageError]: (e: MessageError) =>
      this.comm.alert('danger', e.message),
    [ErrorType.NotFoundError]: (e: NotFoundError) => {
      this.comm.alert(
        'warning',
        // the entity word in the site's terminology; the tracker groups on the raw one
        `This ${this.terms.apply(
          e.entity,
        )} no longer exists. It may have been deleted, or the link may be out of date.`,
        10_000,
      )
      if (e.listRoute) {
        this.router.navigate(e.listRoute)
      }
    },
    [ErrorType.UserFacingError]: (e: unknown) => {
      // server-side extensions.code USER_FACING: written for the user. The
      // phantom-testing flow shows it inline itself and marks it shown.
      const message = getUserFacingMessage(e)
      if (message && !wasUserFacingErrorShown(e)) {
        // sticky: several sentences of instructions, not a 5 s notification
        this.comm.alert('danger', message, -1)
      }
    },
    [ErrorType.AnalysisFailure]: (e: unknown) => {
      // server-side extensions.code ANALYSIS_SYSTEM_ERROR: the analysis
      // failed, and the message tells the technologist whether trying again
      // can help. Shown like a user-facing error: inline by the
      // phantom-testing flow, otherwise as a sticky toast here.
      const message = getAnalysisFailureMessage(e)
      if (message && !wasUserFacingErrorShown(e)) {
        this.comm.alert('danger', message, -1)
      }
    },
  }

  constructor(
    private comm: CommService,
    private router: Router,
    private terms: TerminologyService,
  ) {
    super()
  }

  handleError(e: unknown) {
    if (e instanceof Error && !!(e as any).rejection) {
      e = (e as any).rejection
    }
    super.handleError(e)
    const errorType = this.findErrorType(e)
    // report first: handlers may navigate away, and the report should carry
    // the URL the error happened on
    issueTracker.notify(e)
    // execute the handler for that error type
    this.handlers[errorType](e)
  }

  /**
   * Add logic here to classify your error
   * @param e Error to classify
   */
  private findErrorType(e: unknown): ErrorType {
    if (isAuthError(e)) return ErrorType.AuthError
    // before ApolloError/ServerApolloError, which would otherwise claim it
    if (isUserFacingError(e)) return ErrorType.UserFacingError
    if (isAnalysisFailure(e)) return ErrorType.AnalysisFailure
    if (e instanceof NotFoundError) return ErrorType.NotFoundError
    if (e instanceof MessageError) return ErrorType.MessageError
    if (e instanceof TypeError) return ErrorType.UnknownError
    if (e instanceof Error && isApolloError(e)) return ErrorType.ApolloError
    if (isQueryError(e)) return ErrorType.QueryError
    if (isNetworkError(e)) return ErrorType.NetworkError
    if (isMissingFieldError(e)) return ErrorType.MissingFieldError
    if (isNavError(e)) return ErrorType.NavError
    if (isServerApolloError(e)) return ErrorType.ServerApolloError
    return ErrorType.UnknownError
  }
}
